/**
 * Cover 3 Zone Coverage Rules
 * Pure zone coverage — no man-match principles.
 * Each defender drops to their assigned zone and passes off WRs at zone boundaries.
 *
 * Zone assignments:
 *   CB1  — deep left third
 *   CB2  — deep right third
 *   FS   — deep middle third
 *   DB/LB1 — left flat / curl zone
 *   SS     — right flat / curl zone
 *   LB     — left hook zone (middle)
 *   DB/LB2 — right hook zone (middle)
 */

import { CoverageDefinition, CoverageReaction, Position, DBLBReaction, FSReaction, CB2Reaction, DBLB2Reaction, SSReaction, LBReaction } from '../types';

// ── Starting positions (same alignment as Cover 3 Match) ──────────────

const CB1_COVER3_START: Position = { x: 20, y: 65 };
const DBLB1_COVER3_START: Position = { x: 30, y: 72 };
const FS_COVER3_START: Position = { x: 50, y: 50 };
const CB2_COVER3_START: Position = { x: 80, y: 65 };
const DBLB2_COVER3_START: Position = { x: 55, y: 72 };
const SS_COVER3_START: Position = { x: 70, y: 72 };
const LB_COVER3_START: Position = { x: 45, y: 72 };

// ── CB1 — deep left third zone ────────────────────────────────────────
// CB1 follows any WR that enters the deep left third (x < ~33).
// If the WR crosses CB1's face toward the middle, CB1 hands off and settles back.
// Routes are keyed by the WR's route. The animation engine selects the right one.

const cover3CB1Reactions: CoverageReaction[] = [
  // ── Routes where WR stays in left third — CB1 follows ──
  {
    routeTrigger: 'go',
    wrPositionTrigger: { x: 20, y: 70 },
    cbResponse: [
      { x: 20, y: 40 },
      { x: 20, y: 8 },
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'corner',
    wrPositionTrigger: { x: 20, y: 55 },
    cbResponse: [
      { x: 20, y: 45 },
      { x: 7, y: 18 },
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'deepOut',
    wrPositionTrigger: { x: 20, y: 45 },
    cbResponse: [
      { x: 20, y: 42 },
      { x: 7, y: 40 },
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'comeback',
    wrPositionTrigger: { x: 20, y: 50 },
    cbResponse: [
      { x: 20, y: 27 },
      { x: 17, y: 40 },
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'curl',
    wrPositionTrigger: { x: 20, y: 55 },
    cbResponse: [
      { x: 20, y: 50 },
      { x: 20, y: 35 },
    ],
    responseDelay: 0,
  },
  // ── Routes where WR crosses face to middle — follow then hand off ──
  {
    // Post: WR stems vertical in left third, then breaks inside to middle
    routeTrigger: 'post',
    wrPositionTrigger: { x: 20, y: 55 },
    cbResponse: [
      { x: 20, y: 45 },
      { x: 30, y: 30 },
      { x: 25, y: 28 },
    ],
    responseDelay: 0,
  },
  {
    // Deep In: WR stems vertical in left third, then breaks inside
    routeTrigger: 'deepIn',
    wrPositionTrigger: { x: 20, y: 45 },
    cbResponse: [
      { x: 20, y: 42 },
      { x: 30, y: 40 },
      { x: 25, y: 35 },
    ],
    responseDelay: 0,
  },
  // ── Short routes — no deep threat in zone, drop to deep third ──
  {
    routeTrigger: 'slant',
    wrPositionTrigger: { x: 20, y: 75 },
    cbResponse: [
      { x: 20, y: 50 },
      { x: 20, y: 35 },
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'out',
    wrPositionTrigger: { x: 20, y: 70 },
    cbResponse: [
      { x: 20, y: 50 },
      { x: 20, y: 35 },
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'drag',
    wrPositionTrigger: { x: 20, y: 80 },
    cbResponse: [
      { x: 20, y: 50 },
      { x: 20, y: 35 },
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'hitch',
    wrPositionTrigger: { x: 20, y: 70 },
    cbResponse: [
      { x: 20, y: 50 },
      { x: 20, y: 35 },
    ],
    responseDelay: 0,
  },
  // ── Zone drop — no WR in deep left third ──
  {
    routeTrigger: 'zone_drop',
    wrPositionTrigger: { x: 20, y: 70 },
    cbResponse: [
      { x: 20, y: 50 },
      { x: 20, y: 35 },
    ],
    responseDelay: 0,
  },
];

// ── CB2 — deep right third zone ───────────────────────────────────────
// Mirror of CB1. Follows any WR in the deep right third (x > ~67).
// Hands off WRs that cross face toward the middle.

const cover3CB2Reactions: CB2Reaction[] = [
  // ── Routes where WR stays in right third — CB2 follows ──
  {
    routeTrigger: 'go',
    wrPositionTrigger: { x: 80, y: 70 },
    cbResponse: [
      { x: 80, y: 40 },
      { x: 80, y: 8 },
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'corner',
    wrPositionTrigger: { x: 80, y: 55 },
    cbResponse: [
      { x: 80, y: 45 },
      { x: 93, y: 18 },
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'deepOut',
    wrPositionTrigger: { x: 80, y: 45 },
    cbResponse: [
      { x: 80, y: 42 },
      { x: 93, y: 40 },
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'comeback',
    wrPositionTrigger: { x: 80, y: 50 },
    cbResponse: [
      { x: 80, y: 27 },
      { x: 83, y: 40 },
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'curl',
    wrPositionTrigger: { x: 80, y: 55 },
    cbResponse: [
      { x: 80, y: 50 },
      { x: 80, y: 35 },
    ],
    responseDelay: 0,
  },
  // ── Routes where WR crosses face to middle — follow then hand off ──
  {
    routeTrigger: 'post',
    wrPositionTrigger: { x: 80, y: 55 },
    cbResponse: [
      { x: 80, y: 45 },
      { x: 70, y: 30 },
      { x: 75, y: 28 },
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'deepIn',
    wrPositionTrigger: { x: 80, y: 45 },
    cbResponse: [
      { x: 80, y: 42 },
      { x: 70, y: 40 },
      { x: 75, y: 35 },
    ],
    responseDelay: 0,
  },
  // ── Short routes — no deep threat in zone, drop to deep third ──
  {
    routeTrigger: 'slant',
    wrPositionTrigger: { x: 80, y: 75 },
    cbResponse: [
      { x: 80, y: 50 },
      { x: 80, y: 35 },
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'out',
    wrPositionTrigger: { x: 80, y: 70 },
    cbResponse: [
      { x: 80, y: 50 },
      { x: 80, y: 35 },
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'drag',
    wrPositionTrigger: { x: 80, y: 80 },
    cbResponse: [
      { x: 80, y: 50 },
      { x: 80, y: 35 },
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'hitch',
    wrPositionTrigger: { x: 80, y: 70 },
    cbResponse: [
      { x: 80, y: 50 },
      { x: 80, y: 35 },
    ],
    responseDelay: 0,
  },
  // ── Zone drop — no WR in deep right third ──
  {
    routeTrigger: 'zone_drop',
    wrPositionTrigger: { x: 80, y: 70 },
    cbResponse: [
      { x: 80, y: 50 },
      { x: 80, y: 35 },
    ],
    responseDelay: 0,
  },
];

// ── FS — deep middle third ────────────────────────────────────────────
// FS follows any WR that enters the deep middle third (x ~33-67).
// If the WR crosses FS's face out of the middle to a side third, FS hands off
// and settles back to center. Same follow-and-handoff logic as CB1/CB2.

const cover3FSReactions: FSReaction[] = [
  // ── Post/DeepIn from WR1 (far left, x=20) ──
  {
    // WR1 post: x=20,y=55 → x=50,y=15. FS tracks tight on the WR through the middle.
    routeTrigger: 'post_wr1',
    response: [
      { x: 48, y: 30 },
      { x: 50, y: 17 },
    ],
  },
  {
    // WR1 deepIn: x=20,y=45 → x=60,y=45. FS follows the WR across, hands off at boundary.
    routeTrigger: 'deepIn_wr1',
    response: [
      { x: 45, y: 43 },
      { x: 58, y: 43 },
      { x: 52, y: 35 },
    ],
  },
  // ── Post/DeepIn from WR2 (inner left, x=30, offset) ──
  {
    // WR2 post: x=30,y=60 → x=60,y=20. Enters middle immediately. FS tracks tight.
    routeTrigger: 'post_wr2',
    response: [
      { x: 55, y: 30 },
      { x: 58, y: 18 },
    ],
  },
  {
    // WR2 deepIn: x=30,y=50 → x=70,y=50. FS follows tight across, hands off at boundary.
    routeTrigger: 'deepIn_wr2',
    response: [
      { x: 50, y: 47 },
      { x: 65, y: 47 },
      { x: 55, y: 38 },
    ],
  },
  // ── Post/DeepIn from WR3 (inner right, x=70, mirrored WR2) ──
  {
    // WR3 post: x=70,y=60 → x=40,y=20. Enters middle immediately. FS tracks tight.
    routeTrigger: 'post_wr3',
    response: [
      { x: 45, y: 30 },
      { x: 42, y: 18 },
    ],
  },
  {
    // WR3 deepIn: x=70,y=50 → x=30,y=50. FS follows tight across, hands off at boundary.
    routeTrigger: 'deepIn_wr3',
    response: [
      { x: 50, y: 47 },
      { x: 35, y: 47 },
      { x: 45, y: 38 },
    ],
  },
  // ── Post/DeepIn from WR4 (far right, x=80) ──
  {
    // WR4 post: x=80,y=55 → x=50,y=15. FS tracks tight on the WR through the middle.
    routeTrigger: 'post_wr4',
    response: [
      { x: 52, y: 30 },
      { x: 50, y: 17 },
    ],
  },
  {
    // WR4 deepIn: x=80,y=45 → x=40,y=45. FS follows the WR across, hands off at boundary.
    routeTrigger: 'deepIn_wr4',
    response: [
      { x: 55, y: 43 },
      { x: 42, y: 43 },
      { x: 48, y: 35 },
    ],
  },
  // ── Legacy keys (fallback) ──
  {
    routeTrigger: 'post_left',
    response: [
      { x: 48, y: 30 },
      { x: 50, y: 17 },
    ],
  },
  {
    routeTrigger: 'deepIn_left',
    response: [
      { x: 45, y: 43 },
      { x: 58, y: 43 },
      { x: 52, y: 35 },
    ],
  },
  {
    routeTrigger: 'post_right',
    response: [
      { x: 52, y: 30 },
      { x: 50, y: 17 },
    ],
  },
  {
    routeTrigger: 'deepIn_right',
    response: [
      { x: 55, y: 43 },
      { x: 42, y: 43 },
      { x: 48, y: 35 },
    ],
  },
  // ── Two WRs enter middle from both sides — FS stays centered deep ──
  {
    routeTrigger: 'middle_both',
    response: [
      { x: 50, y: 30 },
      { x: 50, y: 18 },
    ],
  },
  // ── Zone drop — no WR entering middle third ──
  ...['slant', 'out', 'deepOut', 'comeback', 'go', 'corner', 'drag', 'hitch', 'curl', 'zone_drop'].map(route => ({
    routeTrigger: route,
    response: [
      { x: 50, y: 35 },
      { x: 50, y: 20 },
    ] as Position[],
  })),
  // post/deepIn defaults (when no side specified — fallback)
  {
    routeTrigger: 'post',
    response: [
      { x: 50, y: 35 },
      { x: 50, y: 20 },
    ],
  },
  {
    routeTrigger: 'deepIn',
    response: [
      { x: 50, y: 35 },
      { x: 50, y: 20 },
    ],
  },
];

// ── DB/LB1 — left flat / curl zone ───────────────────────────────────
// Drops to the left flat area. If a WR enters the flat, sits on him
// until the WR leaves the zone, then releases back.

const cover3DBLB1Reactions: DBLBReaction[] = [
  {
    // Slant: WR2 goes inside — release, drop to flat zone
    routeTrigger: 'slant',
    response: [
      { x: 28, y: 68 },
      { x: 25, y: 65 },
      { x: 22, y: 60 },
    ],
  },
  {
    // Out: WR2 comes to flat — sit in zone, wall off
    routeTrigger: 'out',
    response: [
      { x: 25, y: 70 },
      { x: 18, y: 70 },
      { x: 12, y: 70 },
    ],
  },
  {
    // Deep Out: WR2 stems vertical then breaks out — drop to flat, let CB take deep
    routeTrigger: 'deepOut',
    response: [
      { x: 28, y: 65 },
      { x: 25, y: 60 },
      { x: 20, y: 55 },
    ],
  },
  {
    // Deep In: WR2 stems vertical then breaks in — drop to flat zone
    routeTrigger: 'deepIn',
    response: [
      { x: 28, y: 65 },
      { x: 25, y: 60 },
      { x: 22, y: 55 },
    ],
  },
  {
    // Comeback: drop to flat zone
    routeTrigger: 'comeback',
    response: [
      { x: 28, y: 65 },
      { x: 25, y: 60 },
      { x: 22, y: 55 },
    ],
  },
  {
    // Go: WR2 goes vertical — drop to flat zone
    routeTrigger: 'go',
    response: [
      { x: 28, y: 65 },
      { x: 25, y: 60 },
      { x: 22, y: 55 },
    ],
  },
  {
    // Post: drop to flat zone
    routeTrigger: 'post',
    response: [
      { x: 28, y: 65 },
      { x: 25, y: 60 },
      { x: 22, y: 55 },
    ],
  },
  {
    // Corner: drop to flat zone
    routeTrigger: 'corner',
    response: [
      { x: 28, y: 65 },
      { x: 25, y: 60 },
      { x: 22, y: 55 },
    ],
  },
  {
    // Drag: WR2 crosses — DB/LB1 stays in flat zone, doesn't chase
    routeTrigger: 'drag',
    response: [
      { x: 28, y: 68 },
      { x: 25, y: 65 },
      { x: 22, y: 60 },
    ],
  },
  {
    // Hitch: WR2 stops short — sit in flat zone
    routeTrigger: 'hitch',
    response: [
      { x: 28, y: 70 },
      { x: 25, y: 68 },
      { x: 22, y: 65 },
    ],
  },
  {
    // Curl: WR2 curls — drop to curl/flat area
    routeTrigger: 'curl',
    response: [
      { x: 28, y: 65 },
      { x: 26, y: 60 },
      { x: 24, y: 58 },
    ],
  },
];

// ── SS — right flat / curl zone (mirrors DB/LB1) ─────────────────────

const cover3SSReactions: SSReaction[] = [
  {
    routeTrigger: 'slant',
    response: [
      { x: 72, y: 68 },
      { x: 75, y: 65 },
      { x: 78, y: 60 },
    ],
  },
  {
    routeTrigger: 'out',
    response: [
      { x: 75, y: 70 },
      { x: 82, y: 70 },
      { x: 88, y: 70 },
    ],
  },
  {
    routeTrigger: 'deepOut',
    response: [
      { x: 72, y: 65 },
      { x: 75, y: 60 },
      { x: 80, y: 55 },
    ],
  },
  {
    routeTrigger: 'deepIn',
    response: [
      { x: 72, y: 65 },
      { x: 75, y: 60 },
      { x: 78, y: 55 },
    ],
  },
  {
    routeTrigger: 'comeback',
    response: [
      { x: 72, y: 65 },
      { x: 75, y: 60 },
      { x: 78, y: 55 },
    ],
  },
  {
    routeTrigger: 'go',
    response: [
      { x: 72, y: 65 },
      { x: 75, y: 60 },
      { x: 78, y: 55 },
    ],
  },
  {
    routeTrigger: 'post',
    response: [
      { x: 72, y: 65 },
      { x: 75, y: 60 },
      { x: 78, y: 55 },
    ],
  },
  {
    routeTrigger: 'corner',
    response: [
      { x: 72, y: 65 },
      { x: 75, y: 60 },
      { x: 78, y: 55 },
    ],
  },
  {
    // Drag: WR3 crosses — SS stays in flat zone, doesn't chase
    routeTrigger: 'drag',
    response: [
      { x: 72, y: 68 },
      { x: 75, y: 65 },
      { x: 78, y: 60 },
    ],
  },
  {
    routeTrigger: 'hitch',
    response: [
      { x: 72, y: 70 },
      { x: 75, y: 68 },
      { x: 78, y: 65 },
    ],
  },
  {
    routeTrigger: 'curl',
    response: [
      { x: 72, y: 65 },
      { x: 74, y: 60 },
      { x: 76, y: 58 },
    ],
  },
];

// ── DB/LB2 — right hook zone ─────────────────────────────────────────
// Sits in the right hook zone. Carries crossers through his zone then
// passes them off at the boundary (same handoff concept as Cover 3 Match).

const cover3DBLB2Reactions: DBLB2Reaction[] = [
  {
    // WR3 slant enters zone from right — carry, then release at boundary
    routeTrigger: 'slant',
    response: [
      { x: 55, y: 68 },
      { x: 52, y: 60 },
      { x: 48, y: 52 },
      { x: 45, y: 48 },
      { x: 50, y: 45 },
      { x: 55, y: 42 },
    ],
  },
  {
    routeTrigger: 'slant_wr4',
    response: [
      { x: 55, y: 68 },
      { x: 50, y: 58 },
      { x: 46, y: 48 },
      { x: 45, y: 44 },
      { x: 50, y: 42 },
      { x: 55, y: 40 },
    ],
  },
  {
    // WR1 slant crosses into zone from left — pick up
    routeTrigger: 'slant_wr1_handoff',
    response: [
      { x: 55, y: 68 },
      { x: 55, y: 60 },
      { x: 55, y: 52 },
      { x: 60, y: 46 },
      { x: 66, y: 42 },
      { x: 73, y: 38 },
    ],
  },
  {
    routeTrigger: 'slant_wr2_handoff',
    response: [
      { x: 55, y: 68 },
      { x: 55, y: 58 },
      { x: 56, y: 48 },
      { x: 62, y: 44 },
      { x: 72, y: 42 },
      { x: 80, y: 40 },
    ],
  },
  {
    // WR3 drag enters zone — carry, release at boundary
    routeTrigger: 'drag',
    response: [
      { x: 55, y: 70 },
      { x: 52, y: 73 },
      { x: 48, y: 75 },
      { x: 45, y: 76 },
      { x: 50, y: 73 },
      { x: 55, y: 70 },
    ],
  },
  {
    routeTrigger: 'drag_wr4',
    response: [
      { x: 55, y: 70 },
      { x: 50, y: 73 },
      { x: 46, y: 74 },
      { x: 45, y: 75 },
      { x: 50, y: 72 },
      { x: 55, y: 70 },
    ],
  },
  {
    // WR1 drag crosses into zone from left — pick up
    routeTrigger: 'drag_wr1_handoff',
    response: [
      { x: 55, y: 70 },
      { x: 55, y: 72 },
      { x: 56, y: 74 },
      { x: 60, y: 75 },
      { x: 66, y: 75 },
      { x: 70, y: 75 },
    ],
  },
  {
    routeTrigger: 'drag_wr2_handoff',
    response: [
      { x: 55, y: 70 },
      { x: 55, y: 74 },
      { x: 58, y: 77 },
      { x: 65, y: 78 },
      { x: 74, y: 80 },
      { x: 80, y: 80 },
    ],
  },
  {
    // Zone drop — no crossers, settle into hook zone
    routeTrigger: 'zone_drop',
    response: [
      { x: 55, y: 68 },
      { x: 55, y: 60 },
      { x: 55, y: 52 },
      { x: 55, y: 45 },
    ],
  },
  // Non-crossing routes: zone drop
  ...['out', 'out_wr4', 'deepOut', 'deepIn', 'comeback', 'go', 'post', 'corner', 'hitch', 'curl'].map(route => ({
    routeTrigger: route,
    response: [
      { x: 55, y: 70 },
      { x: 55, y: 67 },
      { x: 55, y: 62 },
      { x: 55, y: 55 },
      { x: 55, y: 50 },
    ],
  })),
];

// ── LB — left hook zone (mirrors DB/LB2) ─────────────────────────────

const cover3LBReactions: LBReaction[] = [
  {
    // WR1 slant enters zone from left — carry, release at boundary
    routeTrigger: 'slant_wr1',
    response: [
      { x: 45, y: 68 },
      { x: 48, y: 60 },
      { x: 52, y: 52 },
      { x: 55, y: 48 },
      { x: 50, y: 45 },
      { x: 45, y: 42 },
    ],
  },
  {
    routeTrigger: 'slant_wr2',
    response: [
      { x: 45, y: 68 },
      { x: 50, y: 58 },
      { x: 54, y: 48 },
      { x: 55, y: 44 },
      { x: 50, y: 42 },
      { x: 45, y: 40 },
    ],
  },
  {
    // WR3 slant crosses into zone from right — pick up
    routeTrigger: 'slant_wr3_handoff',
    response: [
      { x: 45, y: 68 },
      { x: 45, y: 60 },
      { x: 45, y: 52 },
      { x: 38, y: 48 },
      { x: 28, y: 45 },
      { x: 17, y: 43 },
    ],
  },
  {
    routeTrigger: 'slant_wr4_handoff',
    response: [
      { x: 45, y: 68 },
      { x: 45, y: 58 },
      { x: 44, y: 48 },
      { x: 38, y: 44 },
      { x: 30, y: 40 },
      { x: 25, y: 38 },
    ],
  },
  {
    // WR1 drag enters zone from left — carry, release at boundary
    routeTrigger: 'drag_wr1',
    response: [
      { x: 45, y: 70 },
      { x: 42, y: 74 },
      { x: 48, y: 75 },
      { x: 55, y: 75 },
      { x: 50, y: 72 },
      { x: 45, y: 70 },
    ],
  },
  {
    routeTrigger: 'drag_wr2',
    response: [
      { x: 45, y: 70 },
      { x: 44, y: 76 },
      { x: 50, y: 78 },
      { x: 55, y: 78 },
      { x: 50, y: 74 },
      { x: 45, y: 70 },
    ],
  },
  {
    // WR3 drag crosses into zone from right — pick up
    routeTrigger: 'drag_wr3_handoff',
    response: [
      { x: 45, y: 70 },
      { x: 45, y: 72 },
      { x: 44, y: 74 },
      { x: 40, y: 76 },
      { x: 34, y: 78 },
      { x: 25, y: 80 },
    ],
  },
  {
    routeTrigger: 'drag_wr4_handoff',
    response: [
      { x: 45, y: 70 },
      { x: 45, y: 72 },
      { x: 42, y: 74 },
      { x: 36, y: 75 },
      { x: 30, y: 75 },
      { x: 25, y: 75 },
    ],
  },
  {
    // Zone drop — no crossers
    routeTrigger: 'zone_drop',
    response: [
      { x: 45, y: 68 },
      { x: 45, y: 60 },
      { x: 45, y: 52 },
      { x: 45, y: 45 },
    ],
  },
];

// ── Coverage definition ───────────────────────────────────────────────

export const cover3Definition: CoverageDefinition = {
  id: 'cover3',
  name: 'Cover 3',
  description: 'Cover 3 zone coverage — three deep, four under. Defenders pass off receivers at zone boundaries.',
  initialPosition: CB1_COVER3_START,
  reactionRules: cover3CB1Reactions,
  dblb1InitialPosition: DBLB1_COVER3_START,
  dblb1ReactionRules: cover3DBLB1Reactions,
  fsInitialPosition: FS_COVER3_START,
  fsReactionRules: cover3FSReactions,
  cb2InitialPosition: CB2_COVER3_START,
  cb2ReactionRules: cover3CB2Reactions,
  dblb2InitialPosition: DBLB2_COVER3_START,
  dblb2ReactionRules: cover3DBLB2Reactions,
  ssInitialPosition: SS_COVER3_START,
  ssReactionRules: cover3SSReactions,
  lbInitialPosition: LB_COVER3_START,
  lbReactionRules: cover3LBReactions,
};

// ── Exports ───────────────────────────────────────────────────────────

export {
  CB1_COVER3_START,
  DBLB1_COVER3_START,
  FS_COVER3_START,
  CB2_COVER3_START,
  DBLB2_COVER3_START,
  SS_COVER3_START,
  LB_COVER3_START,
  cover3CB1Reactions,
  cover3CB2Reactions,
  cover3FSReactions,
  cover3DBLB1Reactions,
  cover3DBLB2Reactions,
  cover3SSReactions,
  cover3LBReactions,
};
